import { useEffect } from 'react';

/**
 * Ref-counted scroll lock.
 * Default: iOS-safe body position:fixed (legacy pages).
 * Packers unified mobile: lock `.packers-scroll-region` only — never body position:fixed.
 */
let lockCount = 0;
let savedScrollY = 0;
let savedStyles = null;
let savedRegion = null;
let savedRegionOverflow = '';
let savedRegionTouchAction = '';

function isPackersUnifiedScrollContext() {
    return (
        typeof document !== 'undefined' &&
        document.body.getAttribute('data-packers-unified-toolbar') === 'true'
    );
}

function applyPackersRegionLock() {
    const region = document.querySelector('.packers-scroll-region');
    if (!region) return;
    savedRegion = region;
    savedRegionOverflow = region.style.overflow;
    savedRegionTouchAction = region.style.touchAction;
    region.style.overflow = 'hidden';
    region.style.touchAction = 'none';
    region.setAttribute('data-packers-scroll-locked', 'true');
}

function releasePackersRegionLock() {
    if (!savedRegion) return;
    savedRegion.style.overflow = savedRegionOverflow;
    savedRegion.style.touchAction = savedRegionTouchAction;
    savedRegion.removeAttribute('data-packers-scroll-locked');
    savedRegion = null;
    savedRegionOverflow = '';
    savedRegionTouchAction = '';
}

function applyLock() {
    if (typeof document === 'undefined') return;

    if (isPackersUnifiedScrollContext()) {
        if (lockCount === 0) {
            applyPackersRegionLock();
        }
        lockCount += 1;
        return;
    }

    const html = document.documentElement;
    const { body } = document;
    if (lockCount === 0) {
        savedScrollY = window.scrollY || window.pageYOffset || 0;
        savedStyles = {
            htmlOverflow: html.style.overflow,
            htmlOverscroll: html.style.overscrollBehavior,
            bodyOverflow: body.style.overflow,
            bodyPosition: body.style.position,
            bodyTop: body.style.top,
            bodyLeft: body.style.left,
            bodyRight: body.style.right,
            bodyWidth: body.style.width,
            bodyOverscroll: body.style.overscrollBehavior,
        };
        html.style.overflow = 'hidden';
        html.style.overscrollBehavior = 'none';
        body.style.overflow = 'hidden';
        body.style.overscrollBehavior = 'none';
        body.style.position = 'fixed';
        body.style.top = `-${savedScrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        if (body.classList.contains('packers-mobile-chrome')) {
            body.classList.add('packers-overlay-open');
        }
    }
    lockCount += 1;
}

function releaseLock() {
    if (typeof document === 'undefined') return;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount > 0) return;

    if (isPackersUnifiedScrollContext()) {
        releasePackersRegionLock();
        return;
    }

    const html = document.documentElement;
    const { body } = document;
    body.classList.remove('packers-overlay-open');
    if (savedStyles) {
        html.style.overflow = savedStyles.htmlOverflow;
        html.style.overscrollBehavior = savedStyles.htmlOverscroll;
        body.style.overflow = savedStyles.bodyOverflow;
        body.style.position = savedStyles.bodyPosition;
        body.style.top = savedStyles.bodyTop;
        body.style.left = savedStyles.bodyLeft;
        body.style.right = savedStyles.bodyRight;
        body.style.width = savedStyles.bodyWidth;
        body.style.overscrollBehavior = savedStyles.bodyOverscroll;
        savedStyles = null;
    }
    window.scrollTo(0, savedScrollY);
}

/**
 * Locks document scroll while `locked` is true (mobile menus, drawers, modals).
 */
export function useBodyScrollLock(locked, className = 'scroll-locked') {
    useEffect(() => {
        if (!locked || typeof document === 'undefined') return undefined;

        const { body } = document;
        applyLock();
        if (className) body.classList.add(className);

        return () => {
            if (className) body.classList.remove(className);
            releaseLock();
        };
    }, [locked, className]);
}
